import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Row, Col } from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { getCountryOptions, TWZIP } from '@constants/index';

const { Option } = Select;

interface ContactAddress {
  id: string
  code?: string
  receiver_name?: string
  receiver_phone?: string
  receiver_email?: string
  country_code?: string
  postal_code?: string
  state?: string
  city?: string
  district?: string
  address?: string
  default_sales_channel_id?: string | null
  [key: string]: unknown
}

interface ContactAddressService {
  getList: (params: { search?: string; merchant_id?: string }) => Promise<{ success: boolean; data?: ContactAddress[] }>
  [key: string]: unknown
}

interface FormReceiverInformationProps {
  form: FormInstance
  contactAddressService: ContactAddressService
  merchantId?: string
}

const FormReceiverInformation: React.FC<FormReceiverInformationProps> = ({ form, contactAddressService, merchantId }) => {
  const { t, i18n } = useTranslation();
  const [contactAddresses, setContactAddresses] = useState<ContactAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressesFetched, setAddressesFetched] = useState(false);

  const countryOptions = getCountryOptions(i18n.language);

  // Use Form.useWatch to observe values
  const country = Form.useWatch('country_code', form);
  const city = Form.useWatch('city', form);
  const district = Form.useWatch('district', form);

  // 判斷是否為台灣或空值(預設台灣)
  const isTaiwanOrEmpty = !country || country === '' || country === 'TW';

  // 當城市或區域改變時,自動填入郵遞區號(僅台灣)
  useEffect(() => {
    if (isTaiwanOrEmpty && city && district) {
      const cityData = (TWZIP as Record<string, Record<string, string>>)[city];
      if (cityData && cityData[district]) {
        const autoPostalCode = cityData[district];
        const currentPostalCode = form.getFieldValue('postal_code');
        if (autoPostalCode !== currentPostalCode) {
          form.setFieldsValue({ postal_code: autoPostalCode });
        }
      }
    }
  }, [city, district, isTaiwanOrEmpty, form]);

  // 取得城市選項(台灣)
  const getCityOptions = () => {
    return Object.keys(TWZIP).map(cityName => ({
      value: cityName,
      label: cityName
    }));
  };

  // 取得區域選項(台灣)
  const getDistrictOptions = () => {
    const zipMap = TWZIP as Record<string, Record<string, string>>;
    if (!city || !zipMap[city]) return [];
    return Object.keys(zipMap[city]).map(districtName => ({
      value: districtName,
      label: districtName
    }));
  };

  useEffect(() => {
    setContactAddresses([]);
    setAddressesFetched(false);
  }, [merchantId]);

  const fetchContactAddresses = async (searchText = '') => {
    if (addressesFetched && !searchText) return;

    try {
      setLoadingAddresses(true);
      const data = await contactAddressService.getList({
        search: searchText,
        ...(merchantId ? { merchant_id: merchantId } : {})
      });
      if (data.success) {
        setContactAddresses(data.data || []);
        setAddressesFetched(true);
      }
    } catch (error) {
      console.error('Failed to fetch contact addresses:', error);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleContactAddressSelect = (value: string) => {
    const addressData = contactAddresses.find(addr => addr.id === value);
    if (addressData) {
      form.setFieldsValue({
        country_code: addressData.country_code || form.getFieldValue('country_code'),
        postal_code: addressData.postal_code || '',
        state: addressData.state || '',
        city: addressData.city || '',
        district: addressData.district || '',
        address: addressData.address || '',
        receiver_name: addressData.receiver_name || '',
        phone: addressData.receiver_phone || '',
        email: addressData.receiver_email || form.getFieldValue('email'),
        // 地址簿上的「預設通路」有設定就帶入；沒設定則保留使用者已選的值
        // （sales_channel_id 是 required，不能像 postal_code 那樣 reset 成空）
        sales_channel_id: addressData.default_sales_channel_id || form.getFieldValue('sales_channel_id')
      });
    }
  };

  const handleCityChange = (value: string) => {
    // 清空區域(因為換城市了)
    form.setFieldsValue({
      city: value,
      district: '',
      postal_code: ''
    });
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 4 }}>
          {t(`models.order.contact_address_selector`)}
        </label>
        <Select
          placeholder={t(`common.select.please_select`)}
          onChange={handleContactAddressSelect}
          onFocus={() => fetchContactAddresses()}
          onSearch={fetchContactAddresses}
          allowClear
          style={{ width: '100%' }}
          showSearch
          filterOption={false}
          loading={loadingAddresses}
          notFoundContent={loadingAddresses ? 'Loading...' : 'No addresses found'}
        >
          {contactAddresses.map(addr => (
            <Option key={addr.id} value={addr.id}>
              {`${addr.code} ${addr.receiver_name} ${addr.postal_code || ''} ${addr.city || ''} ${addr.district || ''} ${addr.address}`}
            </Option>
          ))}
        </Select>
      </div>

      <Form.Item
        name="country_code"
        label={t(`models.order.country_code`)}
      >
        <Select
          placeholder={t('common.select.please_select')}
          allowClear
          showSearch
          optionFilterProp="label"
          options={countryOptions}
        />
      </Form.Item>

      <Form.Item name="state" label={t(`models.order.state`)}>
        <Input />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="city"
            label={t(`models.order.city`)}
            getValueProps={(val) => ({ value: isTaiwanOrEmpty ? (val ? [val] : []) : val })}
            normalize={(val) => (Array.isArray(val) ? val[val.length - 1] || '' : val)}
          >
            {isTaiwanOrEmpty ? (
              <Select
                mode="tags"
                maxTagCount={1}
                placeholder={t('common.select.please_select')}
                allowClear
                showSearch
                optionFilterProp="label"
                options={getCityOptions()}
                tokenSeparators={[]}
                onChange={(values) => {
                  const v = Array.isArray(values) ? values[values.length - 1] || '' : values;
                  handleCityChange(v);
                }}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="district"
            label={isTaiwanOrEmpty ? t(`models.order.district`) : t(`models.order.state_province`)}
            getValueProps={(val) => ({ value: isTaiwanOrEmpty ? (val ? [val] : []) : val })}
            normalize={(val) => (Array.isArray(val) ? val[val.length - 1] || '' : val)}
          >
            {isTaiwanOrEmpty ? (
              <Select
                mode="tags"
                maxTagCount={1}
                placeholder={t('common.select.please_select')}
                allowClear
                showSearch
                optionFilterProp="label"
                options={getDistrictOptions()}
                tokenSeparators={[]}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="postal_code" label={t(`models.order.postal_code`)}>
        <Input
          disabled={isTaiwanOrEmpty && !!city && !!district}
          placeholder={isTaiwanOrEmpty && city && district ? t(`common.auto_filled`) : ''}
        />
      </Form.Item>

      <Form.Item
        name="address"
        label={t(`models.order.address`)}
        rules={[{ required: true, message: t('common.validation.required') }]}
      >
        <Input data-testid="order-address-input" />
      </Form.Item>

      <Form.Item
        name="receiver_name"
        label={t(`models.order.receiver_name`)}
        rules={[{ required: true, message: t('common.validation.required') }]}
      >
        <Input data-testid="order-receiver-name-input" />
      </Form.Item>

      <Form.Item
        name="phone"
        label={t(`models.order.phone`)}
        rules={[{ required: true, message: t('common.validation.required') }]}
      >
        <Input data-testid="order-phone-input" />
      </Form.Item>

      <Form.Item name="email" label={t(`models.order.email`)}>
        <Input type="email" />
      </Form.Item>
    </>
  );
};

export default FormReceiverInformation;
